import { useState, useMemo, useEffect, useCallback } from 'react';
import { Edit2, X, Plus, ChevronRight, ChevronDown, AlertTriangle } from 'react-feather';
import { Box } from '../../../components/Box';
import { Modal } from '../../../components/Modal';
import { Colors } from '../../../values/colors';
import { SuccessMessage, ErrorMessage } from '../../../utils/alerts';
import { getFiscalCompanies, updateBillingConfig } from '../../../services/Fiscal';
import {
  PageWrapper, FiltersRow, FilterGroup, FilterLabel, FilterInput, FilterSelect,
  TableWrapper, StyledTable, Th, Td, StatusBadge, RuleTag, ServiceTag, ActionBtn,
  SegmentGroup, SegmentButton, FilterToggle,
  FormSection, FormSectionTitle, FormGrid, FormField, FormLabel,
  FormInput, FormSelect, FormTextarea, TierRow, TierCard, FormHint,
} from '../styles';

const formatMoney = (value) => {
  if (value === '' || value == null) return '';
  const cents = Math.round(Number(value) * 100);
  if (Number.isNaN(cents)) return '';
  return (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const MoneyInput = ({ value, onChange, ...rest }) => {
  const handleChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    if (digits === '') { onChange(''); return; }
   
    onChange(String(Number(digits) / 100));
  };
  return (
    <FormInput
      type="text"
      inputMode="decimal"
      placeholder="0,00"
      value={formatMoney(value)}
      onChange={handleChange}
      {...rest}
    />
  );
};

const BILLING_TYPE_LABEL = {
  per_life:              'Por vida',
  per_life_with_minimum: 'Por vida + valor mínimo',
  fixed:                 'Valor fixo',
  tiers:                 'Por faixa',
  tiers_with_minimum:    'Por faixa com valor mínimo',
};

const BILLING_OPTIONS = Object.entries(BILLING_TYPE_LABEL).map(([value, label]) => ({ value, label }));
const fmt = (v) => v != null ? Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
const emptyServiceTier = () => ({ from: '', to: '', price: '' });


const SERVICE_PRICE_PRESETS = {
  telemedicine: [
    { from: 0.00, to: 1,    individual: 3.75, familiar: 4.15 },
    { from: 1.01, to: 3,    individual: 4.95, familiar: 5.35 },
    { from: 3.01, to: null, individual: 6.20, familiar: 6.60 },
  ],
  teleorientation: [
    { from: 0.00, to: 1,    individual: 2.60, familiar: 3.00 },
    { from: 1.01, to: 3,    individual: 3.80, familiar: 4.20 },
    { from: 3.01, to: null, individual: 4.90, familiar: 5.30 },
  ],
};

const PLAN_OPTIONS = [
  { value: 'individual', label: 'Individual' },
  { value: 'familiar',   label: 'Familiar' },
];

const buildPresetTiers = (service, plan) =>
  (SERVICE_PRICE_PRESETS[service] || []).map(t => ({
    from: t.from,
    to: t.to,
    price: t[plan] ?? '',
  }));

const isEmptyTier = (t) => {
  const empty = (v) => v === '' || v == null;
  return empty(t?.from) && empty(t?.to) && empty(t?.price);
};

const validateTiers = (label, tiers) => {
  const list = (Array.isArray(tiers) ? tiers : []).filter(t => !isEmptyTier(t));
  if (!list.length) return null;

  const num = (v) => (v === '' || v == null ? null : Number(v));
  const parsed = list.map(t => ({ from: num(t.from), to: num(t.to), price: num(t.price) }));

  for (const t of parsed) {
    if (t.from == null || Number.isNaN(t.from)) return `${label}: informe o valor inicial (De) em todas as faixas.`;
    if (t.price == null || Number.isNaN(t.price)) return `${label}: informe o valor (R$) em todas as faixas.`;
    if (t.to != null && !Number.isNaN(t.to) && t.to < t.from) {
      return `${label}: o valor final (Até) não pode ser menor que o inicial (De).`;
    }
  }

  const open = parsed.filter(t => t.to == null);
  if (open.length > 1) return `${label}: só pode haver uma faixa sem limite (Até vazio).`;

  const ordered = [...parsed].sort((a, b) => {
    if (a.to == null) return 1;
    if (b.to == null) return -1;
    return a.from - b.from;
  });

  if (open.length === 1 && ordered[ordered.length - 1].to != null) {
    return `${label}: a faixa sem limite (Até vazio) deve ser a última.`;
  }

  for (let i = 1; i < ordered.length; i++) {
    const prev = ordered[i - 1];
    const curr = ordered[i];
    if (prev.to == null) return `${label}: existe faixa após a faixa sem limite.`;
    if (curr.from <= prev.to) {
      return `${label}: as faixas não podem se sobrepor (${prev.from}–${prev.to} e ${curr.from}–${curr.to == null ? '∞' : curr.to}).`;
    }
  }

  return null;
};

const countTiers = (tiers) => (Array.isArray(tiers) ? tiers : []).filter(t => !isEmptyTier(t)).length;

const validateBillingConfig = (billing) => {
  const hasValue = (v) => v !== '' && v != null && !Number.isNaN(Number(v));
  const { billing_type } = billing;
  const needsPrice   = ['per_life', 'per_life_with_minimum'].includes(billing_type);
  const needsMinimum = ['fixed', 'per_life_with_minimum', 'tiers_with_minimum'].includes(billing_type);
  const needsTiers   = ['tiers', 'tiers_with_minimum'].includes(billing_type);

  if (!billing_type) return 'Selecione um modelo de cobrança.';
  if (needsPrice   && !hasValue(billing.price_per_life)) return 'Informe o preço por vida (R$).';
  if (needsMinimum && !hasValue(billing.minimum_amount)) return 'Informe o valor mínimo (R$).';
  if (needsTiers   && countTiers(billing.tiers) === 0) {
    return 'O modelo por faixa exige ao menos uma faixa configurada.';
  }
  if (billing.has_telemedicine && countTiers(billing.telemedicine_tiers) === 0) {
    return 'Telemedicina: configure ao menos uma faixa preenchida.';
  }
  if (billing.has_teleorientation && countTiers(billing.teleorientation_tiers) === 0) {
    return 'Teleorientação: configure ao menos uma faixa preenchida.';
  }
  return null;
};
const emptyBilling = {
  billing_type: '', price_per_life: '', minimum_amount: '',
  billing_description: '', billing_day: 1,
  tiers: [{ from: 0, to: 999, price: '', description: '' }],
  has_telemedicine: false, has_teleorientation: false,
  telemedicine_tiers: [], teleorientation_tiers: [],
  telemedicine_plan: 'individual', teleorientation_plan: 'individual',
  erp_automation: false, billing_cnpj: false,
  group_minimum_amount: '', due_day: '',
};
const PER_PAGE_OPTIONS = [20, 50, 100];

export const FiscalCompanies = () => {
  const [companies, setCompanies]     = useState([]);
  const [total, setTotal]             = useState(0);
  const [loading, setLoading]         = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch]           = useState('');
  const [page, setPage]               = useState(1);
  const [perPage, setPerPage]         = useState(20);
  const [onlyUnconfigured, setOnlyUnconfigured] = useState(false);
  const [status, setStatus]           = useState(''); // '' | 'active' | 'inactive'
  const [collapsed, setCollapsed]     = useState(new Set());
  const [editTarget, setEditTarget]   = useState(null);
  const [billing, setBilling]         = useState(emptyBilling);
  const [saving, setSaving]           = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getFiscalCompanies({ page, perPage, search, onlyUnconfigured, status });
      setCompanies(data.list || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, onlyUnconfigured, status]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = () => {
    setPage(1);
    setSearch(searchInput);
    setCollapsed(new Set());
  };

  const changeFilter = (setter, value) => {
    setter(value);
    setPage(1);
    setCollapsed(new Set());
  };

  const parentIds = useMemo(() =>
    new Set(companies.filter(c => c.parent_id && companies.find(p => p.id === c.parent_id)).map(c => c.parent_id)),
  [companies]);

  const childrenByParent = useMemo(() => {
    const map = new Map();
    companies.forEach(c => {
      if (c.parent_id == null) return;
      if (!map.has(c.parent_id)) map.set(c.parent_id, []);
      map.get(c.parent_id).push(c);
    });
    return map;
  }, [companies]);

  const hiddenIds = useMemo(() => {
    const hidden = new Set();
    const collect = (id) => {
      (childrenByParent.get(id) || []).forEach(child => {
        hidden.add(child.id);
        collect(child.id);
      });
    };
    collapsed.forEach(id => collect(id));
    return hidden;
  }, [collapsed, childrenByParent]);

  const visibleCompanies = useMemo(
    () => companies.filter(c => !hiddenIds.has(c.id)),
    [companies, hiddenIds]
  );

  const depthOffset = useMemo(
    () => companies.reduce((min, c) => Math.min(min, c.depth ?? 0), 0),
    [companies]
  );

  const pages = Math.max(1, Math.ceil(total / perPage));

  const toggleCollapse = (id) => setCollapsed(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const rootId = useMemo(() => {
    if (!companies.length) return null;
    return companies.reduce((min, c) => (c.depth < min.depth ? c : min), companies[0]).id;
  }, [companies]);

  const collapsibleParentIds = useMemo(
    () => new Set([...parentIds].filter(id => id !== rootId)),
    [parentIds, rootId]
  );

  const expandAll   = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(new Set(collapsibleParentIds));
  const allCollapsed = collapsibleParentIds.size > 0 && collapsed.size >= collapsibleParentIds.size;

  const openEdit = (c) => {
    setEditTarget(c);
    setBilling({
      billing_type:        c.billing_type || '',
      price_per_life:      c.price_per_life ?? '',
      minimum_amount:      c.minimum_amount ?? '',
      billing_description: c.billing_description || '',
      billing_day:         c.billing_day || 1,
      tiers:               c.billing_tiers ? c.billing_tiers.map(t => ({ description: '', ...t })) : [{ from: 0, to: 999, price: '', description: '' }],
      has_telemedicine:    (c.telemedicine_tiers || []).length > 0,
      has_teleorientation: (c.teleorientation_tiers || []).length > 0,
      telemedicine_tiers:  (c.telemedicine_tiers || []).map(t => ({ ...t })),
      teleorientation_tiers: (c.teleorientation_tiers || []).map(t => ({ ...t })),
      telemedicine_plan:    c.telemedicine_plan || 'individual',
      teleorientation_plan: c.teleorientation_plan || 'individual',
      erp_automation:      !!c.erp_automation,
      billing_cnpj:        !!c.billing_cnpj,
      group_minimum_amount: c.group_minimum_amount ?? '',
      due_day:             c.due_day ?? '',
    });
  };

  const handleBillingChange = (key, val) => setBilling(prev => ({ ...prev, [key]: val }));
  const addTier    = () => setBilling(prev => ({ ...prev, tiers: [...prev.tiers, { from: '', to: '', price: '', description: '' }] }));
  const removeTier = (i) => setBilling(prev => ({ ...prev, tiers: prev.tiers.filter((_, idx) => idx !== i) }));
  const updateTier = (i, key, val) => setBilling(prev => ({
    ...prev, tiers: prev.tiers.map((t, idx) => idx === i ? { ...t, [key]: val } : t),
  }));


  const addServiceTier    = (field) => setBilling(prev => ({ ...prev, [field]: [...prev[field], emptyServiceTier()] }));
  const removeServiceTier = (field, i) => setBilling(prev => ({ ...prev, [field]: prev[field].filter((_, idx) => idx !== i) }));
  const updateServiceTier = (field, i, key, val) => setBilling(prev => ({
    ...prev, [field]: prev[field].map((t, idx) => idx === i ? { ...t, [key]: val } : t),
  }));

  const handleToggleService = (flagKey, tiersKey, service, checked) => setBilling(prev => ({
    ...prev,
    [flagKey]: checked,
    [tiersKey]: checked
      ? (prev[tiersKey].length === 0 ? buildPresetTiers(service, prev[`${service}_plan`]) : prev[tiersKey])
      : [],
  }));

  const handleChangeServicePlan = (service, tiersKey, plan) => setBilling(prev => {
    const preset = SERVICE_PRICE_PRESETS[service] || [];
    const sameBound = (a, b) =>
      (a == null || a === '') && (b == null || b === '') ? true : Number(a) === Number(b);
    const tiers = prev[tiersKey].map((tier) => {
      const match = preset.find(p => sameBound(p.from, tier.from) && sameBound(p.to, tier.to));
      return match ? { ...tier, price: match[plan] } : tier;
    });
    return { ...prev, [`${service}_plan`]: plan, [tiersKey]: tiers };
  });

  const handleSave = async () => {
    const { has_telemedicine, has_teleorientation } = billing;
    const usesPriceTiers = ['tiers', 'tiers_with_minimum'].includes(billing.billing_type);

    const error =
      validateBillingConfig(billing) ||
      (usesPriceTiers    ? validateTiers('Preço por faixa', billing.tiers) : null) ||
      (has_telemedicine  ? validateTiers('Faixas de telemedicina', billing.telemedicine_tiers) : null) ||
      (has_teleorientation ? validateTiers('Faixas de teleorientação', billing.teleorientation_tiers) : null);

    if (error) {
      ErrorMessage({ message: error });
      return;
    }

    setSaving(true);
    try {
      const { has_telemedicine: _t, has_teleorientation: _o, ...rest } = billing;
      await updateBillingConfig(editTarget.id, {
        ...rest,
        telemedicine_tiers:    has_telemedicine    ? billing.telemedicine_tiers    : [],
        teleorientation_tiers: has_teleorientation ? billing.teleorientation_tiers : [],
        telemedicine_plan:     has_telemedicine    ? billing.telemedicine_plan     : null,
        teleorientation_plan:  has_teleorientation ? billing.teleorientation_plan  : null,
        group_minimum_amount:  billing.billing_cnpj ? billing.group_minimum_amount : null,
        due_day:               billing.billing_cnpj ? (billing.due_day || null) : null,
      });
      await load();
      setEditTarget(null);
      SuccessMessage({ message: 'Configuração de faturamento salva com sucesso!' });
    } catch (err) {
      const message = err?.response?.data?.message || 'Não foi possível salvar a configuração.';
      ErrorMessage({ message });
    } finally {
      setSaving(false);
    }
  };

  const showPricePerLife = ['per_life', 'per_life_with_minimum'].includes(billing.billing_type);
  const showMinimum      = ['fixed', 'per_life_with_minimum', 'tiers_with_minimum'].includes(billing.billing_type);
  const showTiers        = ['tiers', 'tiers_with_minimum'].includes(billing.billing_type);

  return (
    <PageWrapper>
      <FiltersRow>
        <FilterGroup>
          <FilterLabel>Buscar empresa</FilterLabel>
          <FilterInput
            placeholder="Nome ou CNPJ..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
          />
        </FilterGroup>
        <ActionBtn style={{ alignSelf: 'flex-end' }} onClick={handleSearch}>Buscar</ActionBtn>
        <FilterGroup>
          <FilterLabel>Status</FilterLabel>
          <SegmentGroup>
            <SegmentButton active={status === ''}         onClick={() => changeFilter(setStatus, '')}>Todas</SegmentButton>
            <SegmentButton active={status === 'active'}   onClick={() => changeFilter(setStatus, 'active')}>Ativas</SegmentButton>
            <SegmentButton active={status === 'inactive'} onClick={() => changeFilter(setStatus, 'inactive')}>Inativas</SegmentButton>
          </SegmentGroup>
        </FilterGroup>
        <FilterGroup style={{ justifyContent: 'flex-end' }}>
          <FilterToggle active={onlyUnconfigured} onClick={() => changeFilter(setOnlyUnconfigured, !onlyUnconfigured)}>
            <AlertTriangle size={14} /> Empresas não configuradas
          </FilterToggle>
        </FilterGroup>
        {collapsibleParentIds.size > 0 && (
          <FilterGroup style={{ justifyContent: 'flex-end' }}>
            <FilterToggle onClick={() => (allCollapsed ? expandAll() : collapseAll())}>
              {allCollapsed
                ? <><ChevronDown size={14} /> Expandir empresas</>
                : <><ChevronRight size={14} /> Recolher empresas</>}
            </FilterToggle>
          </FilterGroup>
        )}
        <FilterGroup style={{ marginLeft: 'auto' }}>
          <FilterLabel>Por página</FilterLabel>
          <FilterSelect
            value={perPage}
            onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
            width="90px"
          >
            {PER_PAGE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
          </FilterSelect>
        </FilterGroup>
      </FiltersRow>

      <Box noPadding noHeader>
        <TableWrapper>
          <StyledTable>
            <thead>
              <tr>
                <Th w="52px">ID</Th>
                <Th>Empresa</Th>
                <Th w="150px">CNPJ</Th>
                <Th w="200px" center>Cobrança</Th>
                <Th w="104px" right>Mínimo</Th>
                <Th w="76px" center>Dia fat.</Th>
                <Th w="86px" center>Status</Th>
                <Th w="124px" center>Ações</Th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><Td colSpan={8} style={{ textAlign: 'center', color: Colors.mutedGray }}>Carregando...</Td></tr>
              )}
              {!loading && visibleCompanies.map(c => {
                const depth = c.depth ?? (c.parent_id ? 1 : 0);

                const relDepth = Math.max(0, depth - depthOffset);

                const isRoot = depth === 0;
                const hasChildren = !isRoot && parentIds.has(c.id);
                const isCollapsed = collapsed.has(c.id);
                return (
                  <tr key={c.id} style={{ background: isRoot ? '#f2f4f7' : (relDepth > 1 ? '#fafbfc' : undefined) }}>
                    <Td muted w="52px">{c.id}</Td>
                    <Td>
                      <span style={{ paddingLeft: Math.max(0, relDepth - 1) * 20, display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                        {relDepth > 1 && <span style={{ color: Colors.mutedGray, fontSize: 12, flexShrink: 0 }}>{'└─'}</span>}
                        {hasChildren && (
                          <span onClick={() => toggleCollapse(c.id)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: Colors.mutedGray, flexShrink: 0 }}>
                            {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                          </span>
                        )}
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: isRoot ? 700 : 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.title}>{c.title}</div>
                          <div style={{ fontSize: 12, color: Colors.mutedGray, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.name}>{c.name}</div>
                        </div>
                      </span>
                    </Td>
                    <Td muted w="150px">{c.cnpj}</Td>
                    <Td center w="200px">
                      {c.billing_type ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center', alignItems: 'center' }}>
                          <RuleTag>{BILLING_TYPE_LABEL[c.billing_type]}</RuleTag>
                          {(c.telemedicine_tiers || []).length > 0 && <ServiceTag>Telemed.</ServiceTag>}
                          {(c.teleorientation_tiers || []).length > 0 && <ServiceTag>Teleorient.</ServiceTag>}
                        </div>
                      ) : (
                        <span style={{ fontSize: 12, color: Colors.orange, fontWeight: 600 }}>⚠ Não configurada</span>
                      )}
                    </Td>
                    <Td right w="104px">{c.minimum_amount != null ? fmt(c.minimum_amount) : '—'}</Td>
                    <Td center muted w="76px">{c.billing_day || '—'}</Td>
                    <Td center w="86px">
                      <StatusBadge color={c.active ? 'successGreen' : 'lightGray2'}>
                        {c.active ? 'Ativo' : 'Inativo'}
                      </StatusBadge>
                    </Td>
                    <Td center w="124px">
                      <ActionBtn small variant="outline" onClick={() => openEdit(c)}>
                        <Edit2 size={13} /> Configurar
                      </ActionBtn>
                    </Td>
                  </tr>
                );
              })}
              {!loading && visibleCompanies.length === 0 && (
                <tr><Td colSpan={8} style={{ textAlign: 'center', color: Colors.mutedGray }}>Nenhuma empresa encontrada.</Td></tr>
              )}
            </tbody>
          </StyledTable>
        </TableWrapper>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: `1px solid ${Colors.ultraLightGray2}` }}>
          <span style={{ fontSize: 13, color: Colors.mutedGray }}>
            {total} {(search || onlyUnconfigured || status)
              ? `resultado${total !== 1 ? 's' : ''}`
              : `empresa${total !== 1 ? 's' : ''} (nível principal)`}
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: Colors.mutedGray }}>Página {page} de {pages}</span>
            <ActionBtn small onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹</ActionBtn>
            <ActionBtn small onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}>›</ActionBtn>
          </div>
        </div>
      </Box>

      <Modal
        open={!!editTarget}
        title={`Regra de faturamento — ${editTarget?.title}`}
        onClose={() => setEditTarget(null)}
        containerStyle={{ width: '100%', maxWidth: 680, marginTop: 20, marginBottom: 20 }}
      >
        {editTarget && (
          <div>
            <FormSection style={{ borderTop: 'none', paddingTop: 0, marginTop: 0 }}>
              <FormGrid>
                <FormField style={{ gridColumn: '1 / -1' }}>
                  <FormLabel>Modelo de cobrança</FormLabel>
                  <FormSelect value={billing.billing_type} onChange={e => handleBillingChange('billing_type', e.target.value)}>
                    <option value="">Selecione...</option>
                    {BILLING_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </FormSelect>
                </FormField>
                {showPricePerLife && (
                  <FormField>
                    <FormLabel>Preço por vida (R$)</FormLabel>
                    <MoneyInput value={billing.price_per_life} onChange={v => handleBillingChange('price_per_life', v)} />
                  </FormField>
                )}
                {showMinimum && (
                  <FormField>
                    <FormLabel>Valor mínimo (R$)</FormLabel>
                    <MoneyInput value={billing.minimum_amount} onChange={v => handleBillingChange('minimum_amount', v)} />
                  </FormField>
                )}
                <FormField style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
                    <input type="checkbox" checked={billing.has_telemedicine} onChange={e => handleToggleService('has_telemedicine', 'telemedicine_tiers', 'telemedicine', e.target.checked)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                    <span style={{ fontSize: 14, fontWeight: 600, color: Colors.blueGray }}>Possui telemedicina</span>
                    <span style={{ fontSize: 12, color: Colors.mutedGray }}>(configure as faixas de utilização)</span>
                  </label>
                </FormField>
                <FormField style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
                    <input type="checkbox" checked={billing.has_teleorientation} onChange={e => handleToggleService('has_teleorientation', 'teleorientation_tiers', 'teleorientation', e.target.checked)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                    <span style={{ fontSize: 14, fontWeight: 600, color: Colors.blueGray }}>Possui teleorientação</span>
                    <span style={{ fontSize: 12, color: Colors.mutedGray }}>(configure as faixas de utilização)</span>
                  </label>
                </FormField>
                {!showTiers && (
                  <FormField style={{ gridColumn: '1 / -1' }}>
                    <FormLabel>Descrição da regra</FormLabel>
                    <FormTextarea value={billing.billing_description} onChange={e => handleBillingChange('billing_description', e.target.value)} placeholder="Ex: Contrato possui faturamento mínimo mensal de R$ 500,00." />
                  </FormField>
                )}
              </FormGrid>

              {showTiers && (
                <FormSection>
                  <FormSectionTitle>Preço por faixa</FormSectionTitle>
                  <FormHint style={{ marginBottom: 12 }}>
                    Informe a descrição de cada faixa para detalhar o motivo do valor.
                  </FormHint>
                  {billing.tiers.map((tier, i) => (
                    <TierCard key={i}>
                      <TierRow>
                        <FormField>
                          <FormLabel>De</FormLabel>
                          <FormInput type="number" min="0" value={tier.from} onChange={e => updateTier(i, 'from', e.target.value)} />
                        </FormField>
                        <FormField>
                          <FormLabel>Até (vazio = sem limite)</FormLabel>
                          <FormInput type="number" min="0" placeholder="Sem limite" value={tier.to ?? ''} onChange={e => updateTier(i, 'to', e.target.value === '' ? null : e.target.value)} />
                        </FormField>
                        <FormField>
                          <FormLabel>Valor (R$)</FormLabel>
                          <MoneyInput value={tier.price} onChange={v => updateTier(i, 'price', v)} />
                        </FormField>
                        <ActionBtn small variant="danger" onClick={() => removeTier(i)} style={{ marginBottom: 0 }}><X size={13} /></ActionBtn>
                      </TierRow>
                      <FormField style={{ marginTop: 8 }}>
                        <FormLabel>Descrição da faixa</FormLabel>
                        <FormTextarea
                          value={tier.description ?? ''}
                          onChange={e => updateTier(i, 'description', e.target.value)}
                          placeholder="Ex: Faixa de 0 a 100 vidas — valor promocional do primeiro ano de contrato."
                        />
                      </FormField>
                    </TierCard>
                  ))}
                  <ActionBtn small variant="outline" onClick={addTier} style={{ marginTop: 4 }}><Plus size={13} /> Adicionar faixa</ActionBtn>
                </FormSection>
              )}

              {billing.has_telemedicine && (
                <FormSection>
                  <FormSectionTitle>Faixas de telemedicina (% de utilização)</FormSectionTitle>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <span style={{ fontSize: 13, color: Colors.mutedGray }}>Tabela de preços:</span>
                    {PLAN_OPTIONS.map(opt => (
                      <ActionBtn
                        key={opt.value}
                        small
                        variant={billing.telemedicine_plan === opt.value ? 'primary' : 'outline'}
                        onClick={() => handleChangeServicePlan('telemedicine', 'telemedicine_tiers', opt.value)}
                        style={{ marginBottom: 0 }}
                      >
                        {opt.label}
                      </ActionBtn>
                    ))}
                  </div>
                  {billing.telemedicine_tiers.map((tier, i) => (
                    <TierRow key={i}>
                      <FormField>
                        <FormLabel>De (%)</FormLabel>
                        <FormInput type="number" step="0.01" min="0" placeholder="0" value={tier.from ?? ''} onChange={e => updateServiceTier('telemedicine_tiers', i, 'from', e.target.value)} />
                      </FormField>
                      <FormField>
                        <FormLabel>Até % (vazio = sem limite)</FormLabel>
                        <FormInput type="number" step="0.01" min="0" placeholder="Sem limite" value={tier.to ?? ''} onChange={e => updateServiceTier('telemedicine_tiers', i, 'to', e.target.value === '' ? null : e.target.value)} />
                      </FormField>
                      <FormField>
                        <FormLabel>Valor (R$)</FormLabel>
                        <MoneyInput value={tier.price ?? ''} onChange={v => updateServiceTier('telemedicine_tiers', i, 'price', v)} />
                      </FormField>
                      <ActionBtn small variant="danger" onClick={() => removeServiceTier('telemedicine_tiers', i)} style={{ marginBottom: 0 }}><X size={13} /></ActionBtn>
                    </TierRow>
                  ))}
                  <ActionBtn small variant="outline" onClick={() => addServiceTier('telemedicine_tiers')} style={{ marginTop: 4 }}><Plus size={13} /> Adicionar faixa</ActionBtn>
                </FormSection>
              )}

              {billing.has_teleorientation && (
                <FormSection>
                  <FormSectionTitle>Faixas de teleorientação (% de utilização)</FormSectionTitle>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <span style={{ fontSize: 13, color: Colors.mutedGray }}>Tabela de preços:</span>
                    {PLAN_OPTIONS.map(opt => (
                      <ActionBtn
                        key={opt.value}
                        small
                        variant={billing.teleorientation_plan === opt.value ? 'primary' : 'outline'}
                        onClick={() => handleChangeServicePlan('teleorientation', 'teleorientation_tiers', opt.value)}
                        style={{ marginBottom: 0 }}
                      >
                        {opt.label}
                      </ActionBtn>
                    ))}
                  </div>
                  {billing.teleorientation_tiers.map((tier, i) => (
                    <TierRow key={i}>
                      <FormField>
                        <FormLabel>De (%)</FormLabel>
                        <FormInput type="number" step="0.01" min="0" placeholder="0" value={tier.from ?? ''} onChange={e => updateServiceTier('teleorientation_tiers', i, 'from', e.target.value)} />
                      </FormField>
                      <FormField>
                        <FormLabel>Até % (vazio = sem limite)</FormLabel>
                        <FormInput type="number" step="0.01" min="0" placeholder="Sem limite" value={tier.to ?? ''} onChange={e => updateServiceTier('teleorientation_tiers', i, 'to', e.target.value === '' ? null : e.target.value)} />
                      </FormField>
                      <FormField>
                        <FormLabel>Valor (R$)</FormLabel>
                        <MoneyInput value={tier.price ?? ''} onChange={v => updateServiceTier('teleorientation_tiers', i, 'price', v)} />
                      </FormField>
                      <ActionBtn small variant="danger" onClick={() => removeServiceTier('teleorientation_tiers', i)} style={{ marginBottom: 0 }}><X size={13} /></ActionBtn>
                    </TierRow>
                  ))}
                  <ActionBtn small variant="outline" onClick={() => addServiceTier('teleorientation_tiers')} style={{ marginTop: 4 }}><Plus size={13} /> Adicionar faixa</ActionBtn>
                </FormSection>
              )}
            </FormSection>

            <FormSection>
              <FormGrid cols={2}>
                <FormField style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
                    <input type="checkbox" checked={billing.billing_cnpj} onChange={e => handleBillingChange('billing_cnpj', e.target.checked)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                    <span style={{ fontSize: 14, fontWeight: 600, color: Colors.blueGray }}>CNPJ faturador</span>
                    <span style={{ fontSize: 12, color: Colors.mutedGray }}>(este CNPJ é utilizado como emissor da cobrança)</span>
                  </label>
                </FormField>
                {billing.billing_cnpj && (
                  <>
                    <FormField style={{ gridColumn: '1 / -1' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
                        <input type="checkbox" checked={billing.erp_automation} onChange={e => handleBillingChange('erp_automation', e.target.checked)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                        <span style={{ fontSize: 14, fontWeight: 600, color: Colors.blueGray }}>Ativa na automação ERP</span>
                        <span style={{ fontSize: 12, color: Colors.mutedGray }}>(integração automática com OMIE)</span>
                      </label>
                    </FormField>
                    <FormField>
                      <FormLabel>Dia de faturamento</FormLabel>
                      <FormSelect value={billing.billing_day} onChange={e => handleBillingChange('billing_day', Number(e.target.value))}>
                        <option value={1}>Dia 01</option>
                        <option value={25}>Dia 25</option>
                      </FormSelect>
                    </FormField>
                    <FormField>
                      <FormLabel>Mínimo de grupo (R$)</FormLabel>
                      <MoneyInput value={billing.group_minimum_amount} onChange={v => handleBillingChange('group_minimum_amount', v)} />
                      <span style={{ fontSize: 12, color: Colors.mutedGray, marginTop: 4 }}>
                        Se a soma de toda a árvore ficar abaixo deste valor, cobra o mínimo.
                      </span>
                    </FormField>
                    <FormField>
                      <FormLabel>Dia do vencimento</FormLabel>
                      <FormInput type="number" min="1" max="31" step="1" placeholder="Ex: 10" value={billing.due_day} onChange={e => handleBillingChange('due_day', e.target.value)} />
                      <span style={{ fontSize: 12, color: Colors.mutedGray, marginTop: 4 }}>
                        Dia do mês do vencimento, exibido no texto da OS (Omie).
                      </span>
                    </FormField>
                  </>
                )}
              </FormGrid>
            </FormSection>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
              <ActionBtn onClick={() => setEditTarget(null)}>Cancelar</ActionBtn>
              <ActionBtn variant="primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar configuração'}
              </ActionBtn>
            </div>
          </div>
        )}
      </Modal>
    </PageWrapper>
  );
};
